import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import { capitalize } from 'lodash';
import classNames from 'classnames';
import { DictType, ParagraphDataType, ParagraphType, TemplateData } from '@/model';
import { toTitleCase } from '@/util/stringUtil';
import { convertParagraph } from '@/util/helper/paragraphHelper';
import { useModal, useRouteHandler, useScenarioAutocompletes, useScenarioId } from '@/hook';
import Toolbar from '@/component/Toolbar';
import Tooltip from '@/component/Tooltip';
import { Textarea } from '@/component/input';
import TriangleDownIcon from "@/assets/svgs/triangle_down.svg";
import {
    DeleteIcon, DirectoryIcon, PasteIcon,
    PageFirstIcon, PagePreviousIcon, PageNextIcon, PageLastIcon
} from '@/assets/svgs/icons';

import { EditorParagraph, ParagraphInputsProps } from './model';
import * as ParagraphCardInputs from './cardInputs';
import ParagraphTypeDropdown from './PragraphTypeDropdown';
import { TEMPLATE_CONVERT_MODAL_ID } from './TemplateConvertModal';


const PARAGRAPH_INPUTS_MAP: DictType<React.FC<ParagraphInputsProps<any>>> = {
    'output.text': ParagraphCardInputs.OutputText,
    'output.carousel': ParagraphCardInputs.OutputCarousel,
    'output.media': ParagraphCardInputs.OutputMedia,
    'output.file': ParagraphCardInputs.OutputFile,
    'output.custom': ParagraphCardInputs.OutputCustom,
    'output.llm_answer': ParagraphCardInputs.OutputLLMAnswer,
    'output.raw': ParagraphCardInputs.OutputRaw,
    'utility.variable': ParagraphCardInputs.UtilityVariable,
    'utility.code': ParagraphCardInputs.UtilityCode,
    'utility.api': ParagraphCardInputs.UtilityAPI,
    'input.text': ParagraphCardInputs.InputText,
    'input.list': ParagraphCardInputs.InputList,
    'input.quick_reply': ParagraphCardInputs.InputQuickReply,
    'next.next': ParagraphCardInputs.Next,
    'template.template': ParagraphCardInputs.Template,
} as const;

interface ParagraphCardProps {
    index: number;
    total: number;
    paragraph: EditorParagraph; 
    onToggleOpen: () => void;
    onChange: (paragraph: EditorParagraph) => void;
    onDuplicate: () => void;
    onMoveIndex: (index: number) => void;
    onDelete: () => void;
}

const ParagraphCard: React.FC<ParagraphCardProps> = (props) => {
    const { index, total, paragraph, onToggleOpen, onChange, onDuplicate, onMoveIndex, onDelete } = props;

    const intl = useIntl();
    const condLabelId = useId();
    const { fullAutocomplete } = useScenarioAutocompletes();

    const onChangeParagraphType = (type: ParagraphType, dataType: ParagraphDataType) => {
        const newParagraph = convertParagraph(paragraph, type, dataType);
        onChange({ ...newParagraph, isOpen: true });
    };

    const ParagraphInputs = PARAGRAPH_INPUTS_MAP[`${paragraph.type}.${paragraph.data_type}` as keyof typeof PARAGRAPH_INPUTS_MAP];

    return (
        <div className="size-full flex">
            <div className={classNames(`paragraph-card-accent-${paragraph.type}`)} />
            <div className="size-full px-8 py-6 flex flex-col border border-black-200 rounded-r-[6px] bg-white">
                <ParagraphCardHeader index={index}
                                     total={total}
                                     paragraph={paragraph}
                                     onToggleOpen={onToggleOpen}
                                     onDuplicate={onDuplicate}
                                     onMoveIndex={onMoveIndex}
                                     onDelete={onDelete}
                />

                <div data-testid="paragraph-card-content"
                     aria-hidden={!paragraph.isOpen}
                     className={classNames(
                        "label-input-grid-[120] gap-x-2 gap-y-4",
                        paragraph.isOpen
                            ? "mt-6 opacity-100 transition-[opacity,height] duration-300 ease-in-out"
                            : "h-0 opacity-0 overflow-hidden transition-none"
                    )}
                >
                    <div className="col-span-2 w-full flex items-center">
                        <div className="label-input-grid-[120] w-full gap-x-2">
                            <div className="field-label">
                                {intl.formatMessage({ id: "i04006" })}
                            </div>

                            <ParagraphTypeDropdown type={paragraph.type}
                                                   dataType={paragraph.data_type}
                                                   onChange={onChangeParagraphType}
                            />
                        </div>

                        {paragraph.type === "input" && (
                            <div className="ml-2 w-full flex items-center gap-4">
                                <label className="flex items-center gap-1 cursor-pointer">
                                    <input type="checkbox"
                                           className="input-checkbox"
                                           data-testid="paragraph-card-disable-keyboard"
                                           checked={paragraph.disable_keyboard ?? false}
                                           onChange={e => onChange({ ...paragraph, disable_keyboard: e.target.checked })}
                                    />
                                    <div className="text-[13px] font-[400] text-black-500 leading-none">
                                        {intl.formatMessage({ id: "i04071" })}
                                    </div>
                                </label>
                                {!paragraph.disable_keyboard && (
                                    <label className="flex items-center gap-1 cursor-pointer">
                                        <input type="checkbox"
                                               className="input-checkbox"
                                               data-testid="paragraph-card-masking"
                                               checked={paragraph.masking ?? false}
                                               onChange={e => onChange({ ...paragraph, masking: e.target.checked })}
                                        />
                                        <div className="text-[13px] font-[400] text-black-500 leading-none">
                                            {intl.formatMessage({ id: "i04072" })}
                                        </div>
                                    </label>
                                )}
                            </div>
                        )}
                    </div>

                    <label htmlFor={condLabelId}
                           className="label-input-grid-label gap-x-1 field-label"
                    >
                        {intl.formatMessage({ id: "i04007" })}
                        <Tooltip message={intl.formatMessage({ id: "i02019" })} />
                    </label>
                    <Textarea id={condLabelId}
                              rows={1}
                              autocompletes={fullAutocomplete}
                              defaultValue={paragraph.display_condition}
                              placeholder={intl.formatMessage({ id: "i04008" })}
                              data-testid="paragraph-card-display-condition"
                              onChange={value => onChange({ ...paragraph, display_condition: value })}
                    />

                    {ParagraphInputs ? (
                        <ParagraphInputs paragraph={paragraph}
                                        onChange={onChange}
                        />
                    ) : (
                        <div className="col-span-2">
                            <div className="text-invalid">
                                {intl.formatMessage({ id: "i04083" })}: {paragraph.type} {paragraph.data_type}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


interface ParagraphCardHeaderProps {
    index: number;
    total: number;
    paragraph: EditorParagraph;
    onToggleOpen: () => void;
    onDuplicate: () => void;
    onMoveIndex: (index: number) => void;
    onDelete: () => void;
}

const ParagraphCardHeader: React.FC<ParagraphCardHeaderProps> = (props) => {
    const { index, total, paragraph, onToggleOpen, onDuplicate, onMoveIndex, onDelete } = props;

    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();
    const { moveToTemplateEdit } = useRouteHandler();
    
    const onClickTemplateSetting = () => {
        if (paragraph.type === "template" && !(paragraph.data as TemplateData)?.template_id) {
            return;
        }
        if (paragraph.type !== "template") {
            const { isOpen, ...rest } = paragraph;
            modal.open(TEMPLATE_CONVERT_MODAL_ID, rest);
        } else {
            moveToTemplateEdit(scenarioId, { state: { selectedTemplateId: paragraph.data?.template_id } });
        }
    };

    return (
        <div className="grid grid-cols-[120px_1fr] gap-x-2">
            <div className="flex items-center tabular-nums">
                <Tooltip placement="top"
                         offset={4}
                         message={
                            paragraph.isOpen
                                ? intl.formatMessage({ id: "b0019" })
                                : intl.formatMessage({ id: "b0018" })
                         }
                >
                    <button type="button"
                            data-testid="paragraph-card-toggle-open"
                            aria-expanded={paragraph.isOpen}
                            aria-label={intl.formatMessage({ id: paragraph.isOpen ? "b0019" : "b0018" })}
                            onClick={onToggleOpen}
                    >
                        <img src={TriangleDownIcon}
                             alt=""
                             aria-hidden="true"
                             className={classNames(
                                 "size-3.5 transition-transform duration-200",
                                 paragraph.isOpen ? "rotate-180" : "rotate-0"
                             )}
                        />
                    </button>
                </Tooltip>
                <div className="ml-2 text-black-500 text-[14px] font-[500] leading-none">
                    {`#${index + 1}`}
                </div>
                <div className={classNames("ml-3", `paragraph-card-badge-${paragraph.type}`)}>
                    {capitalize(paragraph.type)}
                </div>
            </div>

            <div className="min-w-0 flex justify-between items-center">
                <Toolbar aria-label={intl.formatMessage({ id: "i04090" })}
                         className="flex-none flex items-center gap-1.5"
                >
                    <Tooltip placement="top"
                             offset={4}
                             message={
                                 paragraph.type === "template"
                                     ? intl.formatMessage({ id: "i04003" })
                                     : intl.formatMessage({ id: "i04004" })
                             }
                    >
                        <button type="button"
                                data-testid="paragraph-card-template"
                                aria-label={intl.formatMessage({ id: paragraph.type === "template" ? "i04003" : "i04004" })}
                                onClick={onClickTemplateSetting}
                        >
                            <DirectoryIcon className="size-4 text-black-500 hover:opacity-50" />
                        </button>
                    </Tooltip>
                    <Tooltip placement="top"
                            offset={4}
                            message={intl.formatMessage({ id: "i04005" })}
                    >
                        <button type="button"
                                data-testid="paragraph-card-duplicate"
                                aria-label={intl.formatMessage({ id: "i04005" })}
                                onClick={onDuplicate}
                        >
                            <PasteIcon className="size-4 text-black-500 hover:opacity-50" />
                        </button>
                    </Tooltip>
                    <button type="button"
                            data-testid="paragraph-card-delete"
                            aria-label={intl.formatMessage({ id: "b0006" })}
                            onClick={onDelete}
                    >
                        <DeleteIcon className="size-4 text-point-red hover:text-point-red-50" />
                    </button>

                    {!paragraph.isOpen && (
                        <div className="min-w-0 ml-2 text-[14px] text-black-500 truncate">
                            {`${toTitleCase(paragraph.type)} > ${toTitleCase(paragraph.data_type)}`}
                        </div>
                    )}
                </Toolbar>

                <Toolbar aria-label={intl.formatMessage({ id: "i04091" })}
                         className="flex items-center gap-1 text-black-500"
                >
                    <Tooltip placement="top"
                             offset={2}
                             message={intl.formatMessage({ id: "i04009" })}
                    >
                        <button type="button"
                                data-testid="paragraph-card-move-first"
                                disabled={index === 0}
                                className="disabled:opacity-50 hover:opacity-50"
                                aria-label={intl.formatMessage({ id: "i04009" })}
                                onClick={() => onMoveIndex(0)}
                        >
                            <PageFirstIcon className="size-4 scale-150 origin-center rotate-90" />
                        </button>
                    </Tooltip>
                    <Tooltip placement="top"
                             offset={2}
                             message={intl.formatMessage({ id: "i04010" })}
                    >
                        <button type="button"
                                data-testid="paragraph-card-move-last"
                                disabled={index === total - 1}
                                className="disabled:opacity-50 hover:opacity-50"
                                aria-label={intl.formatMessage({ id: "i04010" })}
                                onClick={() => onMoveIndex(total - 1)}
                        >
                            <PageLastIcon className="size-4 scale-150 origin-center rotate-90" />
                        </button>
                    </Tooltip>
                    <Tooltip placement="top"
                             offset={2}
                             message={intl.formatMessage({ id: "i04011" })}
                             disable={index === 0}
                    >
                        <button type="button"
                                data-testid="paragraph-card-move-prev"
                                disabled={index === 0}
                                className="disabled:opacity-50 hover:opacity-50"
                                aria-label={intl.formatMessage({ id: "i04011" })}
                                onClick={() => onMoveIndex(index - 1)}
                        >
                            <PagePreviousIcon className="size-4 scale-150 origin-center rotate-90" />
                        </button>
                    </Tooltip>
                    <Tooltip placement="top"
                             offset={2}
                             message={intl.formatMessage({ id: "i04012" })}
                             disable={index === total - 1}
                    >
                        <button type="button"
                                data-testid="paragraph-card-move-next"
                                disabled={index === total - 1}
                                className="disabled:opacity-50 hover:opacity-50"
                                aria-label={intl.formatMessage({ id: "i04012" })}
                                onClick={() => onMoveIndex(index + 1)}
                        >
                            <PageNextIcon className="size-4 scale-150 origin-center rotate-90" />
                        </button>
                    </Tooltip>
                </Toolbar>
            </div>
        </div>
    );
};

export default React.memo(ParagraphCard);
